// Admin API service

import { API_BASE_URL as API } from '../config/apiConfig';

// Token management
let authToken = localStorage.getItem('adminToken');

export const setAuthToken = (token) => {
  authToken = token;
  if (token) {
    localStorage.setItem('adminToken', token);
  } else {
    localStorage.removeItem('adminToken');
  }
};

export const getAuthToken = () => authToken;

export const isAuthenticated = () => !!authToken;

// Auth headers helper
const authHeaders = () => ({
  'Content-Type': 'application/json',
  ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
});

// ============== Auth ==============
export const adminLogin = async (email, password) => {
  const response = await fetch(`${API}/admin/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Login failed');
  }
  
  const data = await response.json();
  setAuthToken(data.token);
  return data;
};

export const adminLogout = () => {
  setAuthToken(null);
};

export const getCurrentAdmin = async () => {
  const response = await fetch(`${API}/admin/me`, {
    headers: authHeaders(),
  });
  
  if (!response.ok) {
    throw new Error('Not authenticated');
  }
  
  return response.json();
};

// ============== Orders ==============
export const getOrders = async (status = null) => {
  const params = status ? `?status=${status}` : '';
  const response = await fetch(`${API}/admin/orders${params}`, {
    headers: authHeaders(),
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch orders');
  }
  
  return response.json();
};

export const getOrderById = async (orderId) => {
  const response = await fetch(`${API}/admin/orders/${orderId}`, {
    headers: authHeaders(),
  });
  
  if (!response.ok) {
    throw new Error('Order not found');
  }
  
  return response.json();
};

export const updateOrderStatus = async (orderId, status) => {
  const response = await fetch(`${API}/admin/orders/${orderId}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify({ status }),
  });
  
  if (!response.ok) {
    throw new Error('Failed to update order status');
  }
  
  return response.json();
};

// ============== Gallery ==============
export const getGalleryItems = async () => {
  const response = await fetch(`${API}/admin/gallery`, {
    headers: authHeaders(),
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch gallery items');
  }
  
  return response.json();
};

export const createGalleryItem = async (item) => {
  const response = await fetch(`${API}/admin/gallery`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(item),
  });
  
  if (!response.ok) {
    throw new Error('Failed to create gallery item');
  }
  
  return response.json();
};

export const updateGalleryItem = async (itemId, updates) => {
  const response = await fetch(`${API}/admin/gallery/${itemId}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(updates),
  });
  
  if (!response.ok) {
    throw new Error('Failed to update gallery item');
  }
  
  return response.json();
};

export const deleteGalleryItem = async (itemId) => {
  const response = await fetch(`${API}/admin/gallery/${itemId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  
  if (!response.ok) {
    throw new Error('Failed to delete gallery item');
  }
  
  return response.json();
};

export default {
  adminLogin,
  adminLogout,
  getCurrentAdmin,
  getOrders,
  getOrderById,
  updateOrderStatus,
  getGalleryItems,
  createGalleryItem,
  updateGalleryItem,
  deleteGalleryItem,
  isAuthenticated,
  setAuthToken,
  getAuthToken,
};
